import { Button, Checkbox, Label, QuickLink } from "components";
import FileUpload, {
	DRIVERS_LICENSE_ACCEPTED_FILE_TYPES,
	splitFileUploads,
} from "elements/Input/FileUpload";
import { useEffect, useRef, useState } from "react";
import client from "utils/client";
import { sendErrorToWebhook } from "utils/sendWebhook";
import { usePropertyContext } from "../../context";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import Image from "next/image";
import FileUploadErrorIcon from "public/file-upload-error.svg";
import FileUploadSubmittingAnimation from "public/file-upload-submitting-animation.gif";
import FileUploadSuccessAnimation from "public/file-upload-success-animation.gif";
import axios from "axios";
import { useAuthContext } from "context/AuthContext";
import ButtonV2 from "elements/ButtonV2";

const OwnerDL = () => {
	const [error, setError] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [success, setSuccess] = useState(false);
	const [verificationError, setVerificationError] = useState(false);
	const [showTimeoutBtn, setShowTimeoutBtn] = useState(false);
	const {
		next,
		ownerDlFilesToUpload,
		setOwnerDlFilesToUpload,
		ownerDlExemptValue,
		setOwnerDlExemptValue,
		ownerHasUploadedDlFile,
		setOwnerHasUploadedDlFile,
		setOwnerDlVerificationErrored,
	} = useExemptionsContext();
	const { details } = usePropertyContext();
	const { userData } = useAuthContext();
	const trackEvent = useEventTracker();

	const [fileUploaded, setFileUploaded] = useState(ownerHasUploadedDlFile);

	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	const verifyDriversLicense = ({ propertyId }: { propertyId: number }) => {
		if (!details?.id) return;
		setOwnerDlVerificationErrored(false);
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}
		intervalRef.current = setInterval(async () => {
			try {
				const response = await client.verifyExemptionsDriversLicense({
					propertyId: details?.id,
				});

				if (
					(response?.data.status === "success" ||
						response?.data.status === "failure") &&
					intervalRef.current
				) {
					clearInterval(intervalRef.current);
					setSubmitting(false);
					if (
						response?.data.status === "success" &&
						response?.data.validatedLicenses.owner1?.is_valid
					) {
						setSuccess(true);
						trackEvent({
							eventName:
								constants.EVENTS.Exemptions_Flow_Drivers_License_OCR_Success,
							data: {
								"Customer ID": userData?.id,
								"User Number": "1",
							},
						});
					} else {
						setVerificationError(true);
						trackEvent({
							eventName:
								constants.EVENTS.Exemptions_Flow_Drivers_License_OCR_Failed,
							data: {
								"Customer ID": userData?.id,
								"User Number": "1",
								"Failed Reason": "DL Verification Failed",
							},
						});
					}
				} else if (response?.status !== 200 && intervalRef.current) {
					setSubmitting(false);
					setError(true);
					clearInterval(intervalRef.current);
				}
			} catch (e) {
				if (axios.isAxiosError(e) && intervalRef.current) {
					setSubmitting(false);
					setError(true);
					clearInterval(intervalRef.current);
					trackEvent({
						eventName:
							constants.EVENTS.Exemptions_Flow_Drivers_License_OCR_Failed,
						data: {
							"Customer ID": userData?.id,
							"User Number": "1",
							"Failed Reason": "Internal Error",
						},
					});
				}
			}
		}, 3000);
	};

	const handleSubmit = async () => {
		if (!details) return;
		setError(false);
		setVerificationError(false);

		trackEvent({
			eventName: constants.EVENTS.Exemptions_Flow_Drivers_License_1_Answer,
			data: {
				"Action Taken": ownerDlExemptValue ? "Exempt" : "Uploaded",
				"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
					? "Yes"
					: "No",
			},
		});
		if (success || !!details?.exemption?.owner1_license_ref) {
			const trackingEventData = {
				"Action Taken": ownerDlExemptValue ? "Exempt" : "Uploaded",
			};
			next({ trackingEventData });
			return;
		}

		try {
			if (ownerDlFilesToUpload.length > 0) {
				setSubmitting(true);

				const fd = new FormData();
				fd.append("license1", ownerDlFilesToUpload[0].file);

				await client.submitExemptionsDriverLicense({
					formData: fd,
					propertyId: details!.id,
				});
				setFileUploaded(true);

				await client.sendVerificationRequestExemptionsDriversLicense({
					propertyId: details?.id,
					owner1: true,
					owner2: false,
				});
				verifyDriversLicense({ propertyId: details?.id });
			}
		} catch (e) {
			console.error("Exemptions - Error uploading owner driver license: ", e);
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_Drivers_License_OCR_Failed,
				data: {
					"Customer ID": userData?.id,
					"User Number": "1",
					"Failed Reason": "Internal Error",
				},
			});
			sendErrorToWebhook(
				"** Error Uploading files for Exemptions driver license ** " + e
			);
			setOwnerDlVerificationErrored(true);
			setSubmitting(false);
			setVerificationError(false);
			setError(true);
		}
	};

	useEffect(() => {
		let timeout: NodeJS.Timeout | null = null;
		if (submitting) {
			timeout = setTimeout(() => {
				if (intervalRef.current) {
					setShowTimeoutBtn(true);
				}
			}, 30000);
		}
		return () => {
			if (timeout && !submitting) {
				clearTimeout(timeout);
				if (intervalRef?.current) {
					clearInterval(intervalRef.current);
				}
			}
		};
	}, [submitting]);

	const handleSkip = () => {
		trackEvent({
			eventName: constants.EVENTS.Exemptions_Flow_Drivers_License_1_Answer,
			data: {
				"Action Taken": "Skipped",
				"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
					? "Yes"
					: "No",
				"Verification Error": verificationError,
			},
		});
		const trackingEventData = {
			"Action Taken": "Skipped",
			"Verification Error": verificationError,
		};
		next({ trackingEventData });
	};

	const handleTimeoutProceedButton = () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}
		next();
	};

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			{submitting ? (
				<div id="exemptions-file-submitting">
					<Image
						src={FileUploadSubmittingAnimation}
						alt="File upload submitting animation"
					/>
					{showTimeoutBtn ? (
						<>
							<p className="mt-2 mb-4">
								This is taking longer than we expected, feel free to proceed
								without verifying
							</p>
							<ButtonV2
								variant="primary-outline"
								size="small"
								onClick={() => handleTimeoutProceedButton()}
								className="exemptions-show-timeout-button secondary">
								Proceed
							</ButtonV2>
						</>
					) : (
						<>
							<p className="mt-2 mb-4">
								Give us a few moments to verify your driver’s license.
							</p>
							<p className="sm">Please do not close or refresh this page.</p>
						</>
					)}
				</div>
			) : success || ownerHasUploadedDlFile ? (
				<LicenseUploadModalSuccess />
			) : !!details?.exemption?.owner1_license_ref ? (
				<LicenseUploadModalSuccess alreadyUploaded />
			) : (
				<>
					<div className="property-profile-question-card exemption-survey-card">
						<div className="property-profile-question-card-title-container">
							<p>
								Upload a copy of <u>only the front</u> of your driver’s license.
							</p>
						</div>
						<p className="exemption-question-card-subtitle sm mb-2">
							Counties will require driver’s licenses to be provided as proof of
							ownership.
						</p>
						<FileUpload
							label="Upload front of driver’s license"
							numOfFilesAccepted={1}
							accept={DRIVERS_LICENSE_ACCEPTED_FILE_TYPES.join(",")}
							filesToUpload={ownerDlFilesToUpload}
							updateFilesToUpload={fn => {
								setOwnerDlFilesToUpload(fn(ownerDlFilesToUpload));
							}}
						/>
						{/* <div className="mt-4">
					<Checkbox
						name="ownerDlExemptValue"
						label="I’m exempt from having to provide the required documentation."
						labelEl="p"
						onChange={() => {
							setOwnerDlExemptValue(!ownerDlExemptValue);
							setOwnerDlFile(undefined);
						}}
						checked={ownerDlExemptValue}
					/>
				</div> */}
					</div>
					{!verificationError && (
						<div className="exemptions-azure-card mb-4">
							<p className="sm white">
								The address on the driver's license must match the address of
								this residence.
							</p>
						</div>
					)}
				</>
			)}
			{!fileUploaded && error && (
				<p className="sm text-center terracotta mt-4">
					Whoops, something went wrong. Please try again.
				</p>
			)}
			{verificationError &&
				ExemptionsIssueVerifyingDriverLicenseMesssage(
					details?.auth_first_name,
					details?.auth_last_name
				)}
			<ButtonV2
				size="large"
				disabled={
					submitting ||
					!(
						ownerDlFilesToUpload.length > 0 ||
						ownerDlExemptValue ||
						success ||
						details?.exemption?.owner1_license_ref
					)
				}
				type="submit"
				className="exemptions-continue-button">
				{submitting
					? "Verifying..."
					: details?.exemption?.owner1_license_ref || success
						? "Continue"
						: "Verify Driver's License"}
			</ButtonV2>
			{success ||
				!!details?.exemption?.owner1_license_ref ||
				(!submitting && (
					<div className="exemption-survey-skip">
						{fileUploaded ? (
							<>
								<ButtonV2
									onClick={() => handleSkip()}
									variant="primary-outline"
									size="small"
									className="exemptions-continue-button"
									style={{ marginTop: "16px" }}>
									Proceed With My Upload
								</ButtonV2>
							</>
						) : (
							<QuickLink asButton onClick={() => handleSkip()}>
								Upload Later
							</QuickLink>
						)}
					</div>
				))}
		</form>
	);
};

export const LicenseUploadModalSuccess = ({
	alreadyUploaded = false,
	owner,
}: {
	alreadyUploaded?: boolean;
	owner?: string;
}) => {
	return (
		<div id="property-documents-upload-modal-file-upload-container">
			<div id="property-documents-upload-modal-success-animation-container">
				<Image
					src={FileUploadSuccessAnimation}
					alt="File upload success animation"
					className="property-documents-upload-modal-success-animation"
				/>
			</div>
			<div>
				<p className="lg bold text-center mb-2">
					{alreadyUploaded
						? `${
								owner ? <span className="no-translate">owner</span> : "Your"
							} driver's license has been received`
						: "Your driver’s license have been received and approved!"}
				</p>
			</div>
		</div>
	);
};

export const ExemptionsIssueVerifyingDriverLicenseMesssage = (
	firstName: string | undefined,
	lastName: string | undefined
) => (
	<div
		id="exemptions-issue-verifying-driver-license-message"
		className="sm exemptions-notification-message-rust mt-4"
		style={{ marginTop: "32px" }}>
		<p className="cloud">
			We had an issue verifying the Driver's License for{" "}
			{firstName ? (
				<span className="no-translate">{firstName}</span>
			) : (
				"this user"
			)}{" "}
			{lastName ? <span className="no-translate">{lastName}</span> : ""}. You
			can try self verifying again by uploading a new one, or proceed with your
			upload and our team will review it for you.
		</p>
		<p className="cloud sm mt-4">
			Below are some reasons there might have been an error.
		</p>

		<ul>
			<li className="cloud sm">
				Make sure you are uploading only a Driver's License, not a passport or
				document.
			</li>
			<li className="cloud sm">
				The Driver's License address must match the address for which you are
				applying for exemptions.
			</li>
			<li className="ml-2 cloud sm">
				If you are on Active Duty serving in the armed forces, you may proceed
				with your upload and we will verify your information.
			</li>
			<li className="cloud sm">
				The name on the Driver's License must match the application name:{" "}
				<span className="no-translate">
					{firstName && lastName ? firstName + " " + lastName : ""}
				</span>
				.
			</li>
			<li className="cloud sm">
				Only upload an image of the front. You do not need to provide an image
				of the back.
			</li>
			<li className="cloud sm">
				If you are uploading multiple drivers licenses, do not upload the same
				Driver's License twice.
			</li>
			<li className="cloud sm">
				Ensure the image is clear and there is no glare from bright lights.
			</li>
			<li className="cloud sm">
				Try placing the driver's license on a flat solid-colored background.
			</li>
		</ul>
	</div>
);

export default OwnerDL;
